import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { NoData, Spinner } from '@rio-cloud/rio-uikit';
import { fetchVehicleWarrantyHistory, type VehicleWarrantyHistory } from '../../../../../api/tas';

const columns = [
    'intl-msg:support.vehicleHistory.columns.chassis',
    'intl-msg:support.vehicleHistory.columns.partCode',
    'intl-msg:support.vehicleHistory.columns.causingPart',
    'intl-msg:support.vehicleHistory.columns.partsQuantity',
    'intl-msg:support.vehicleHistory.columns.dealer',
    'intl-msg:support.vehicleHistory.columns.createdAt',
];

interface Props {
    chassis?: string;
}

export default function WarrantyHistory({ chassis }: Props) {
    const intl = useIntl();
    const [rows, setRows] = useState<VehicleWarrantyHistory[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        if (!chassis) {
            setRows([]);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setHasError(false);

        fetchVehicleWarrantyHistory(chassis)
            .then(data => {
                if (!cancelled) {
                    setRows(data);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setRows([]);
                    setHasError(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [chassis]);

    if (isLoading) {
        return (
            <div className='display-flex justify-content-center padding-20'>
                <Spinner isDoubleSized />
            </div>
        );
    }

    if (hasError || !rows.length) {
        return (
            <NoData
                text={intl.formatMessage({
                    id: hasError
                        ? 'intl-msg:support.vehicleHistory.loadError'
                        : 'intl-msg:support.vehicleHistory.noData',
                })}
                className='text-size-16 padding-20'
            />
        );
    }

    return (
        <div className='table-responsive'>
            <table className='table table-head-filled'>
                <thead>
                    <tr>
                        {columns.map(column => (
                            <th key={column}>{intl.formatMessage({ id: column })}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => (
                        <tr key={row.id}>
                            <td>{chassis}</td>
                            <td>{row.partNumber}</td>
                            <td>{row.causalPartIndicator}</td>
                            <td>{row.partQuantity}</td>
                            <td>{row.dealer}</td>
                            <td>
                                {row.createdAt ? moment.utc(row.createdAt).local().format('DD/MM/YYYY HH:mm') : '-'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
